<template>
    <div class="scatter-chart">
        <VueApexChart
            v-if="chartOptions"
            ref="chart"
            class="app-chart-scatter"
            :class="{ 'live' : autoUpdate }"
            width="100%"
            height="100%"
            :options="chartOptions"
            :series="series"
        />

        <div class="legend-container">
            <div class="item-wrapper">
                <div class="circle positive"></div>
                <p class="label">Positive</p>
            </div>
            <div class="item-wrapper">
                <div class="circle neutral"></div>
                <p class="label">Neutral</p>
            </div>
            <div class="item-wrapper">
                <div class="circle negative"></div>
                <p class="label">Negative</p>
            </div>
        </div>
    </div>
</template>

<script>
export default {
	props: {
		strokeColor: {
			type: String,
			default: '#54D8FF'
		},

		sessionStart: {
			type: [String, Date],
			default: () => new Date()
		},

		sessionEnd: {
			type: [String, Date],
			default: () => new Date()
		},

		labels: {
			type: Array,
			default: () => []
		},

		series: {
			type: Array,
			default: () => []
		},

		annotations: {
			type: Object,
			default: () => {}
		},

		autoUpdate: {
			type: Boolean,
			default: false
		},

		// define the visible minutes
		xAxisRange: {
			type: Number,
			default: 60
		},

		// define minimum value for x-axis if no data is provided
		xMinValue: {
			type: [String, Date],
			default: () => new Date()
		},

		toolbar: {
			type: Boolean,
			default: true
		},

		formatTooltip: {
			type: Boolean,
			default: true
		},

		showStartToEnd: {
			type: Boolean,
			default: false
		},

		title: String,
		tooltipTitle: String,
		noData: {
			type: String,
			default: 'No Data'
		}
	},

	watch: {
		labels (newVal, oldVal) {
			if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
				this.init()
			}
		},

		series () {
			this.init()
		},

		annotations () {
			this.init()
		}
	},

	async created () {
		// add watcher for render related props
		this.$watch(vm => [
			vm.autoUpdate,
			vm.xAxisRange,
			vm.xMinValue,
			vm.toolbar,
			vm.formatTooltip,
			vm.title,
			vm.tooltipTitle,
			vm.noData
		], async () => {
			this.chartOptions = null
			await this.init()
			if (!this.autoUpdate) { this.setDefaultZoom() }
		})

		await this.init()
		if (!this.autoUpdate) {
			this.setDefaultZoom()
		}
	},

	data () {
		return {
			chartOptions: null
		}
	},

	computed: {
		rangeInMinutes () {
			return this.xAxisRange * 60000
		}
	},

	methods: {
		getXTick () {
			if (this.series.length && this.series[0].data && this.series[0].data.length) {
				const data = this.series[0].data
				// const min = new Date(data[0][0]).getTime()
				const max = new Date(data[data.length - 1].x).getTime()
				// const diff = max - min
				// const isBased = diff <= this.rangeInMinutes
				return {
					// min: isBased ? min : max - this.rangeInMinutes,
					min: max - this.rangeInMinutes,
					max: max
				}
			}

			return {
				min: this.xMinValue.getTime(),
				max: this.xMinValue.getTime() + this.rangeInMinutes
			}
		},

		async init () {
			const xTick = await this.getXTick()

			this.chartOptions = {
				chart: {
					type: 'scatter',
					toolbar: {
						show: !this.autoUpdate && this.toolbar,
						tools: {
							download: false,
							reset: false,
							customIcons: [{
								icon: '<span style="font-size: 16px; margin-left: 5px;">&times;</span>',
								index: 4,
								title: 'Reset',
								class: 'custom-icon',
								click: () => {
									this.setDefaultZoom()
								}
							}]
						},
						autoSelected: 'pan'
					},
					animations: {
						easing: 'linear',
						speed: 500,
						animateGradually: { delay: 500 },
						dynamicAnimation: { speed: 500 }
					}
				},
				annotations: this.series.length > 0 ? {
					...this.annotations,
					yaxis: [
						{
							y: 1,
							strokeDashArray: 0,
							borderColor: '#44B1ED'
						}
					]
				} : this.annotations,
				title: {
					text: this.title,
					margin: 0,
					style: {
						color: 'currentColor',
						fontWeight: 'normal',
						fontSize: '11px',
						fontFamily: 'Quicksand, sans-serif'
					}
				},
				dataLabels: { enabled: false },
				stroke: {
					width: 2,
					curve: 'smooth',
					colors: this.strokeColor
				},
				markers: {
					size: 8
				},
				grid: {
					background: '#fff',
					borderColor: '#eee',
					padding: { left: 15, bottom: 15 }
				},

				tooltip: {
					enabled: !this.autoUpdate,
					fixed: { enabled: true, offsetY: -20 },
					style: { fontSize: '12px' },
					marker: { show: false },
					x: {
						show: true,
						format: 'MMM dd, yyyy hh:mm TT'
					},
					y: {
						formatter: (val) => {
							if (!this.formatTooltip || !this.labels.length) return val
							return this.labels[val]
						},
						title: { formatter: () => this.tooltipTitle }
					}
				},
				xaxis: {
					type: 'datetime',
					labels: {
						datetimeUTC: false, // true to use UTC
						datetimeFormatter: {
							hour: 'h TT',
							minute: 'h:mm TT',
							second: 'h:mm:00 TT'
						},
						style: { fontSize: '10px' }
					},
					tooltip: { enabled: false }
				},
				yaxis: {
					seriesName: this.series.length ? this.series[0].name : undefined,
					tickAmount: 6,
					min: -0.8,
					max: 2.8,
					reversed: true
				},
				noData: {
					text: this.noData,
					align: 'center',
					verticalAlign: 'middle',
					style: { fontSize: '10px' }
				}
			}

			if ((this.autoUpdate && this.annotations.xaxis.length > 0) || (this.autoUpdate && this.series.length > 0)) {
				this.chartOptions = {...this.chartOptions,
					...{
						xaxis: {
							min: xTick.min,
							max: xTick.max,
							range: this.rangeInMinutes,
							type: 'datetime',
							labels: {
								datetimeUTC: false, // true to use UTC
								datetimeFormatter: {
									hour: 'h TT',
									minute: 'h:mm TT',
									second: 'h:mm:00 TT'
								},
								style: { fontSize: '10px' }
							},
							tooltip: { enabled: false }
						}
					}}
			}
		},

		getColor (value) {
			if (value > 1.6) return '#BB5E36'
			if (value > 0.4) return '#F3DF46'
			return '#9ACD32'
		},

		setDefaultZoom () {
			if (!this.series.length || !this.series[0].data.length) return

			if (this.showStartToEnd) {
				const seriesData = this.series[0].data
				const lastData = new Date(seriesData[seriesData.length - 1].x).getTime()
				const startData = new Date(seriesData[0].x).getTime()
				this.$refs.chart.zoomX(startData, lastData)
				return
			}

			if (this.sessionStart !== new Date()) {
				this.$refs.chart.zoomX(new Date(this.sessionStart).getTime(), new Date(this.sessionEnd).getTime())
				return
			}

			const seriesData = this.series[0].data
			const lastData = new Date(seriesData[seriesData.length - 1].x).getTime()
			const startData = new Date(seriesData[0].x).getTime()
			const diff = lastData - startData
			const isBased = diff <= this.rangeInMinutes
			const end = lastData
			const start = isBased ? end - this.rangeInMinutes : startData
			this.$refs.chart.zoomX(start, end)
		}
	}
}
</script>

<style lang="scss" scoped>
.scatter-chart {
    width: 100%;
    height: 90%;
    min-height: 0;
    min-width: 0;
    text-transform: capitalize;
    position: relative;
    display: flex;
    flex-direction: column;

    .live {
        pointer-events: none;
    }

    .legend-container {
        display: flex;
        justify-content: center;

        .item-wrapper {
            margin-right: 80px;
            display: flex;
            align-items: center;

            &:last-child {
                margin-right: 0;
            }

            .circle {
                border-radius: 50%;
                height: 24px;
                width: 24px;
                margin-right: 12px;

                &.positive {
                    background-color: #9ACD32;
                }

                &.neutral {
                    background-color: #F3DF46;
                }

                &.negative {
                    background-color: #BB5E36;
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppChartScatter.vue